import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PhotoCard from '../components/PhotoCard';
import CategoryFilter from '../components/CategoryFilter';
import PhotoOfTheWeek from '../components/PhotoOfTheWeek';
import { useAuth } from '../context/AuthContext';
import { Loader2, Sparkles, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePhotoGrid = () => {
  const { getAuthHeader, user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchNews = async (category = 'all') => {
    try {
      setLoading(true);
      const params = category !== 'all' ? { category } : {};
      const response = await axios.get(`${API}/news`, {
        params,
        headers: getAuthHeader()
      });
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(selectedCategory);
  }, [selectedCategory]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleBookmarkToggle = async (newsId, isBookmarked) => {
    if (!user) {
      toast.error('Please login to bookmark');
      return;
    }
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${newsId}`, {
          headers: getAuthHeader()
        });
        toast.success('Bookmark removed');
      } else {
        await axios.post(`${API}/bookmarks?news_id=${newsId}`, {}, {
          headers: getAuthHeader()
        });
        toast.success('Bookmark added');
      }
      fetchNews(selectedCategory);
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Sparkles className="w-6 h-6 text-[#FF6B6B]" strokeWidth={2.5} />
            <h1 className="text-4xl md:text-5xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Today's Wit
            </h1>
            <Sparkles className="w-6 h-6 text-[#4ECDC4]" strokeWidth={2.5} />
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Tap any photo for the funny take 📸
          </p>
        </div>

        {/* Photo of the Week */}
        <PhotoOfTheWeek />

        {/* Category Filter */}
        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
        />

        {/* Photo Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" strokeWidth={2.5} />
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 dark:text-gray-400">
              No news available yet. Check back soon!
            </p>
          </div>
        ) : (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            data-testid="photo-grid"
          >
            {news.map((item) => (
              <PhotoCard
                key={item.id}
                news={item}
                onBookmarkToggle={handleBookmarkToggle}
              />
            ))}
          </div>
        )}

        {/* Footer hint */}
        <div className="text-center mt-8 py-4">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            <LayoutGrid className="w-4 h-4 inline mr-1" />
            {news.length} stories • Tap to reveal the wit
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePhotoGrid;
